export default function ComparisonChecklist() {
  const questions = [
    {
      question: 'Are you licensed and insured for custom home construction in Texas?',
      why: 'Texas requires custom home builders to be registered and carry comprehensive insurance including general liability and workers\' compensation. Verify their license with the Texas Residential Construction Commission. Proper insurance protects you from liability during the lengthy construction process.',
      redFlag: 'Builders who can\'t provide current license numbers and insurance certificates should be avoided immediately.'
    },
    {
      question: 'How many custom homes have you built in El Paso?',
      why: 'Local experience is crucial for understanding El Paso\'s unique challenges including caliche soil, building codes, climate considerations, and reliable subcontractors. Ask to see a portfolio of completed homes and visit recent projects to assess quality firsthand.',
      redFlag: 'Be cautious of builders new to El Paso who may not understand local conditions and requirements.'
    },
    {
      question: 'Can you provide references from recent clients?',
      why: 'Recent references demonstrate current work quality and customer satisfaction. Contact at least 3 references to ask about communication, timeline adherence, budget management, and overall satisfaction. Ask if they would hire the builder again—this is the ultimate test.',
      redFlag: 'Builders who can\'t provide recent local references or seem hesitant may have dissatisfied customers.'
    },
    {
      question: 'What is your design and planning process?',
      why: 'Quality builders have structured design processes including initial consultations, architectural planning, material selections, and detailed specifications. They should explain how they incorporate your vision while ensuring functionality, code compliance, and budget adherence. Clear processes prevent costly changes during construction.',
      redFlag: 'Vague descriptions of the design process or rushing through planning stages often lead to problems later.'
    },
    {
      question: 'Who will manage my project daily?',
      why: 'Understanding project management is critical for custom homes. Ask about the project manager, their experience, and how often they\'ll be on-site. Quality builders provide consistent supervision, coordinate subcontractors, and maintain communication throughout the 6-12 month construction process.',
      redFlag: 'Builders who can\'t identify a dedicated project manager or seem disorganized raise concerns about quality control.'
    },
    {
      question: 'What is your typical timeline from start to completion?',
      why: 'Custom home construction in El Paso typically takes 8-14 months from groundbreaking to move-in, depending on size and complexity. Realistic timelines account for design, permitting, weather, and inspections. Builders should provide a detailed schedule with milestone dates and explain factors that could cause delays.',
      redFlag: 'Unrealistically short timelines or reluctance to commit to schedules indicate poor planning or experience.'
    },
    {
      question: 'How do you handle the budget and payment schedule?',
      why: 'Custom homes require detailed budgets itemizing all costs including materials, labor, permits, and contingencies. Payment schedules should tie to construction milestones with draws inspected before payment. Quality builders provide transparent accounting and communicate about any potential cost changes immediately.',
      redFlag: 'Vague budgets, large upfront deposits, or resistance to detailed cost breakdowns are major red flags.'
    },
    {
      question: 'What warranties do you provide?',
      why: 'Texas law requires builders to provide warranties on new construction. Structural warranties typically cover 10 years, major systems 2 years, and workmanship 1 year. Get all warranty terms in writing including what\'s covered, exclusions, and claim procedures. Quality builders stand behind their work.',
      redFlag: 'Builders offering minimal warranties or only verbal promises lack confidence in their construction quality.'
    },
    {
      question: 'How do you handle changes and upgrades during construction?',
      why: 'Changes during construction are common but must be managed properly. Professional builders use written change orders detailing scope, cost, and timeline impacts. They should explain their change order process and how they handle unexpected issues discovered during construction.',
      redFlag: 'Builders who discourage questions about changes or don\'t use formal change orders often lead to disputes.'
    },
    {
      question: 'What makes your homes energy-efficient for El Paso\'s climate?',
      why: 'Energy efficiency is critical in El Paso where cooling costs dominate. Quality builders incorporate enhanced insulation, high-performance windows, efficient HVAC systems, and proper orientation. They should explain specific features and estimated energy savings. Energy-efficient construction costs more upfront but saves thousands annually.',
      redFlag: 'Builders who can\'t discuss energy efficiency specifics or dismiss its importance may deliver homes with high operating costs.'
    }
  ];

  return (
    <section id="checklist" className="py-20 px-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            10 Questions to Ask Your El Paso Custom Home Builder
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Use this comprehensive checklist when interviewing custom home builders. These critical questions help you identify qualified professionals and avoid costly mistakes. Building a custom home is a major investment—choose wisely.
          </p>
        </div>

        <div className="bg-white rounded-xl border-2 border-teal-600 p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">How to Use This Checklist</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Interview at least 3 custom home builders before making your decision. Ask each question and take detailed notes on their responses. Pay attention to their communication style, professionalism, and willingness to answer thoroughly. Compare responses to identify the builder best suited for your dream home.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {questions.map((item, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-lg flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                    
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-2">
                          <i className="ri-information-line"></i>
                          Why This Matters
                        </p>
                        <p className="text-sm text-blue-900 leading-relaxed">{item.why}</p>
                      </div>
                      
                      <div className="bg-red-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-red-900 mb-1 flex items-center gap-2">
                          <i className="ri-alert-line"></i>
                          Red Flag Warning
                        </p>
                        <p className="text-sm text-red-900 leading-relaxed">{item.redFlag}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            Additional Tips for Choosing a Custom Home Builder
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-white/90">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Visit Model Homes:</strong> Tour completed homes to assess quality, design aesthetic, and attention to detail. This reveals the builder\'s standards better than photos.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Check Online Reviews:</strong> Research builders on Google, BBB, and Houzz for consistent positive feedback. Look for how they handle complaints.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Review Contracts Carefully:</strong> Have an attorney review the construction contract before signing. Ensure everything discussed is documented.</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Understand Financing:</strong> Secure construction financing before starting. Builders should work with your lender and understand draw schedules.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Trust Your Instincts:</strong> Building a custom home requires a strong working relationship. Choose a builder you trust and communicate well with.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Document Everything:</strong> Take photos throughout construction. Keep all plans, permits, warranties, and correspondence organized.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
